import { useEffect, useMemo, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { LinearScreen } from '@/features/linear-canvas/LinearScreen';
import { FlowNote } from '@/features/monitoring/FlowNote';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DateRangePicker,
  EmptyState,
  FilterSelect,
  LoadingState,
  Pagination,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TenantSelector,
} from '@/components/ui';
import { manualBroadcastsApi, type BroadcastJournalEntry } from '@/api/manualBroadcasts';
import { getChannelDisplayName } from '@/config/channels';
import { BroadcastHistoryViewSelector, type BroadcastHistoryView } from '@/features/broadcast-history/components/BroadcastHistoryViewSelector';
import { QUEUE_SOURCE_OPTIONS, getQueueSourceLabel } from '@/features/broadcast-queue/queueSourceRegistry';
import { useTenantScope } from '@/hooks/useTenantScope';
import { formatDateTime, formatPhone } from '@/utils/formatters';

const PAGE_SIZE = 30;

const CHANNEL_OPTIONS = [
  { value: 'whatsapp_waba', label: 'WhatsApp WABA' },
  { value: 'whatsapp_regular', label: 'WhatsApp Regular' },
  { value: 'telegram_bot', label: 'Telegram Bot' },
  { value: 'telegram_client', label: 'Telegram Client' },
  { value: 'max', label: 'MAX' },
  { value: 'sms', label: 'SMS' },
];

const TRANSPORT_STATUS_OPTIONS = [
  { value: 'sent', label: 'Отправлено' },
  { value: 'delivered', label: 'Доставлено' },
  { value: 'read', label: 'Прочитано' },
  { value: 'failed', label: 'Ошибка' },
  { value: 'skipped', label: 'Пропущено' },
  { value: 'unknown', label: 'Неизвестно' },
];

const BEHAVIORAL_STATUS_OPTIONS = [
  { value: 'responded', label: 'Ответил' },
  { value: 'read', label: 'Прочитал' },
  { value: 'action_not_completed', label: 'Нет действия' },
  { value: 'delivered_not_read', label: 'Доставлено без чтения' },
  { value: 'sent_not_delivered', label: 'Не доставлено' },
  { value: 'failed', label: 'Ошибка' },
  { value: 'not_applicable', label: 'Не применяется' },
];

const LAUNCH_ORIGIN_OPTIONS = [
  { value: 'manually', label: 'Вручную' },
  { value: 'cron', label: 'Cron' },
];

function getTransportVariant(status: string): 'success' | 'warning' | 'error' | 'info' | 'secondary' {
  switch (status) {
    case 'read':
    case 'delivered':
      return 'success';
    case 'sent':
      return 'info';
    case 'failed':
      return 'error';
    case 'skipped':
      return 'warning';
    default:
      return 'secondary';
  }
}

function getBehavioralVariant(status: string): 'success' | 'warning' | 'error' | 'info' | 'secondary' {
  switch (status) {
    case 'responded':
    case 'read':
      return 'success';
    case 'action_not_completed':
    case 'delivered_not_read':
      return 'warning';
    case 'failed':
      return 'error';
    case 'sent_not_delivered':
      return 'info';
    default:
      return 'secondary';
  }
}

function getLaunchOriginLabel(origin?: string): string {
  if (origin === 'cron') return 'Cron';
  if (origin === 'manually') return 'Вручную';
  return origin || '—';
}

function getSourceLabel(item: BroadcastJournalEntry): string {
  if (item.view_type === 'auto') {
    const key = item.source_event_type || item.source_key || item.broadcast_group || item.source_pipeline;
    if (key) {
      return getQueueSourceLabel(key) || item.source_label || key;
    }
  }
  return item.source_label || '—';
}

export function ManualBroadcastHistoryPage() {
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant, requireTenantSelection } = useTenantScope();

  const [view, setView] = useState<BroadcastHistoryView>('manual');
  const [items, setItems] = useState<BroadcastJournalEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [channelFilter, setChannelFilter] = useState('');
  const [transportStatusFilter, setTransportStatusFilter] = useState('');
  const [behavioralStatusFilter, setBehavioralStatusFilter] = useState('');
  const [launchOriginFilter, setLaunchOriginFilter] = useState('');
  const [sourceKeyFilter, setSourceKeyFilter] = useState('');

  useEffect(() => {
    setOffset(0);
  }, [view, tenantId, searchQuery, dateFrom, dateTo, channelFilter, transportStatusFilter, behavioralStatusFilter, launchOriginFilter, sourceKeyFilter]);

  useEffect(() => {
    if (!tenantId && isSuperAdmin) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await manualBroadcastsApi.getUnifiedHistory({
          tenant_id: tenantId || undefined,
          view_type: view,
          search: searchQuery || undefined,
          date_from: dateFrom ? dateFrom.toISOString() : undefined,
          date_to: dateTo ? dateTo.toISOString() : undefined,
          channel: channelFilter || undefined,
          transport_status: transportStatusFilter || undefined,
          behavioral_status: behavioralStatusFilter || undefined,
          launch_origin: launchOriginFilter || undefined,
          source_key: view === 'auto' ? (sourceKeyFilter || undefined) : undefined,
          limit: PAGE_SIZE,
          offset,
        });
        if (!cancelled) {
          setItems(res.items);
          setTotal(res.total);
        }
      } catch {
        if (!cancelled) {
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, isSuperAdmin, view, searchQuery, dateFrom, dateTo, channelFilter, transportStatusFilter, behavioralStatusFilter, launchOriginFilter, sourceKeyFilter, offset]);

  const headerActions = (
    isSuperAdmin ? (
      <TenantSelector
        value={selectedTenantId ?? ''}
        onChange={setSelectedTenant}
        required={false}
        showLabel={false}
      />
    ) : undefined
  );

  const summary = useMemo(() => {
    return {
      sent: items.filter((item) => item.transport_status === 'sent').length,
      delivered: items.filter((item) => item.transport_status === 'delivered' || item.transport_status === 'read').length,
      fallback: items.filter((item) => item.fallback_used).length,
      responded: items.filter((item) => item.behavioral_status === 'responded').length,
    };
  }, [items]);

  return (
    <MainLayout headerTitle="История отправок" disableMainScroll>
      <LinearScreen
        eyebrow="Рассылки"
        title="История отправок"
        lead="Журнал ручных отправок выбранной клиники: транспорт, fallback и ответ пациента."
        toolbar={headerActions}
      >
      <div className="space-y-4">
        <FlowNote id="history" />
        {requireTenantSelection ? (
          <EmptyState
            type="noData"
            title="Выберите клинику"
            description="Для просмотра журнала отправок сначала выберите клинику."
          />
        ) : (
          <>
            <BroadcastHistoryViewSelector value={view} onChange={setView} />

            <div className="grid gap-4 md:grid-cols-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-sm text-neutral-500">Всего на странице</div>
                  <div className="mt-2 text-2xl font-semibold text-neutral-900">{items.length}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-sm text-neutral-500">Доставлено/прочитано</div>
                  <div className="mt-2 text-2xl font-semibold text-green-700">{summary.delivered}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-sm text-neutral-500">Fallback использован</div>
                  <div className="mt-2 text-2xl font-semibold text-amber-700">{summary.fallback}</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-sm text-neutral-500">Есть ответ</div>
                  <div className="mt-2 text-2xl font-semibold text-blue-700">{summary.responded}</div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Фильтры</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <SearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Поиск по пациенту, телефону, шаблону..."
                    className="w-full md:w-80"
                    size="sm"
                    debounceMs={400}
                  />

                  <FilterSelect
                    value={channelFilter}
                    onChange={setChannelFilter}
                    options={CHANNEL_OPTIONS}
                    placeholder="Все каналы"
                    size="sm"
                  />

                  <FilterSelect
                    value={transportStatusFilter}
                    onChange={setTransportStatusFilter}
                    options={TRANSPORT_STATUS_OPTIONS}
                    placeholder="Тех. статус"
                    size="sm"
                  />

                  <FilterSelect
                    value={behavioralStatusFilter}
                    onChange={setBehavioralStatusFilter}
                    options={BEHAVIORAL_STATUS_OPTIONS}
                    placeholder="Поведение"
                    size="sm"
                  />

                  <FilterSelect
                    value={launchOriginFilter}
                    onChange={setLaunchOriginFilter}
                    options={LAUNCH_ORIGIN_OPTIONS}
                    placeholder="Источник запуска"
                    size="sm"
                  />

                  {view === 'auto' && (
                    <FilterSelect
                      value={sourceKeyFilter}
                      onChange={setSourceKeyFilter}
                      options={QUEUE_SOURCE_OPTIONS}
                      placeholder="Источник авторассылки"
                      size="sm"
                    />
                  )}

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSearchQuery('');
                      setDateFrom(null);
                      setDateTo(null);
                      setChannelFilter('');
                      setTransportStatusFilter('');
                      setBehavioralStatusFilter('');
                      setLaunchOriginFilter('');
                      setSourceKeyFilter('');
                    }}
                  >
                    Сбросить
                  </Button>
                </div>

                <DateRangePicker
                  from={dateFrom}
                  to={dateTo}
                  onChange={(from, to) => {
                    setDateFrom(from);
                    setDateTo(to);
                  }}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  {view === 'manual' ? 'Ручной журнал' : 'Автоматический журнал'} ({total})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <LoadingState text="Загрузка журнала отправок..." />
                ) : items.length === 0 ? (
                  <EmptyState
                    type="noData"
                    title="Нет отправок"
                    description="По выбранным фильтрам ничего не найдено."
                  />
                ) : (
                  <>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeader>Источник</TableHeader>
                          <TableHeader>Пациент</TableHeader>
                          <TableHeader>Телефон</TableHeader>
                          <TableHeader>Шаблон</TableHeader>
                          <TableHeader>Запуск</TableHeader>
                          <TableHeader>Канал</TableHeader>
                          <TableHeader>Fallback</TableHeader>
                          <TableHeader>Тех. статус</TableHeader>
                          <TableHeader>Поведение</TableHeader>
                          <TableHeader>Отправка</TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="max-w-xs">
                              <div className="font-medium text-neutral-900">{getSourceLabel(item)}</div>
                              {item.source_pipeline && view === 'auto' && (
                                <div className="text-xs text-neutral-500">{item.source_pipeline}</div>
                              )}
                            </TableCell>
                            <TableCell>{item.patient_name || '—'}</TableCell>
                            <TableCell className="font-mono text-sm">{formatPhone(item.phone)}</TableCell>
                            <TableCell>{item.template_name || '—'}</TableCell>
                            <TableCell>
                              <Badge variant={item.launch_origin === 'cron' ? 'info' : 'secondary'}>
                                {getLaunchOriginLabel(item.launch_origin)}
                              </Badge>
                            </TableCell>
                            <TableCell>{item.channel ? getChannelDisplayName(item.channel) : '—'}</TableCell>
                            <TableCell>
                              {item.fallback_used ? (
                                <div className="text-sm">
                                  <div className="font-medium text-amber-700">Да</div>
                                  <div className="text-neutral-500">{item.fallback_channel ? getChannelDisplayName(item.fallback_channel) : 'без канала'}</div>
                                </div>
                              ) : (
                                'Нет'
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <Badge variant={getTransportVariant(item.transport_status)}>
                                  {item.transport_status}
                                </Badge>
                                {item.error_reason && (
                                  <div className="max-w-xs truncate text-xs text-red-600" title={item.error_reason}>
                                    {item.error_reason}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <Badge variant={getBehavioralVariant(item.behavioral_status)}>
                                  {item.behavioral_label || item.behavioral_status}
                                </Badge>
                                {item.interaction_type && (
                                  <div className="text-xs text-neutral-500">{item.interaction_type}</div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>{formatDateTime(item.sent_at)}</div>
                              {item.delivered_at && (
                                <div className="text-xs text-neutral-500">
                                  Доставлено: {formatDateTime(item.delivered_at)}
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {total > PAGE_SIZE && (
                      <div className="mt-4">
                        <Pagination
                          offset={offset}
                          limit={PAGE_SIZE}
                          total={total}
                          onOffsetChange={setOffset}
                          showPageNumbers
                        />
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
      </LinearScreen>
    </MainLayout>
  );
}
